import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import { surveyAPI } from '../../services/api';
import { 
  ArrowLeft, BarChart3, Download, Eye, FileText, 
  TrendingUp, Users, MessageSquare 
} from 'lucide-react';
import { toast } from 'react-toastify';
import Table from '../../components/UI/Table';

const Container = styled.div`
  padding: 2rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: ${props => props.theme.typography.fontSize['2xl']};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text.primary};
  margin: 0;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const StatCard = styled(Card)`
  padding: 1.5rem;
`;

const StatLabel = styled.div`
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};
  margin-bottom: 0.5rem;
`;

const StatValue = styled.div`
  font-size: ${props => props.theme.typography.fontSize['2xl']};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.text.primary};
`;

const QuestionSection = styled.div`
  margin-bottom: 2rem;
`;

const QuestionTitle = styled.h3`
  font-size: ${props => props.theme.typography.fontSize.lg};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text.primary};
  margin: 0 0 1rem 0;
`;

const ResponseCard = styled(Card)`
  padding: 1rem;
  margin-bottom: 1rem;
`;

const ResponseInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: start;
  margin-bottom: 0.5rem;
`;

const ResponseText = styled.div`
  font-size: ${props => props.theme.typography.fontSize.base};
  color: ${props => props.theme.colors.text.primary};
  margin-top: 0.5rem;
`;

const RespondentInfo = styled.div`
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};
`;

const OptionCount = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  background-color: ${props => props.theme.colors.background.secondary};
  border-radius: ${props => props.theme.borderRadius.md};
  margin-bottom: 0.5rem;
`;

const SurveyResponses = () => {
  const { theme } = useTheme();
  const { surveyId } = useParams();
  const navigate = useNavigate();
  const [survey, setSurvey] = useState(null);
  const [responses, setResponses] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('responses'); // 'responses' or 'statistics'

  useEffect(() => {
    if (surveyId) {
      fetchSurveyData();
      fetchResponses();
      fetchStatistics();
    }
  }, [surveyId]);

  const fetchSurveyData = async () => {
    try {
      const response = await surveyAPI.getById(surveyId);
      setSurvey(response.data);
    } catch (error) {
      console.error('Error fetching survey:', error);
      toast.error('Failed to load survey');
    }
  };

  const fetchResponses = async () => {
    try {
      setLoading(true);
      // Fetch grouped responses
      const response = await surveyAPI.getResponses(surveyId, { all: true });
      const responseData = response.data.data || [];
      
      console.log('Fetched grouped responses:', responseData);
      console.log('Submissions count:', responseData.length);
      
      if (responseData.length > 0 && responseData[0].responses) {
        console.log('Sample submission:', {
          respondent: responseData[0].respondent_name,
          email: responseData[0].respondent_email,
          responses_count: responseData[0].responses.length,
          first_response: responseData[0].responses[0]
        });
      }
      
      setResponses(Array.isArray(responseData) ? responseData : []);
    } catch (error) {
      console.error('Error fetching responses:', error);
      toast.error('Failed to load responses');
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await surveyAPI.getStatistics(surveyId);
      setStatistics(response.data);
    } catch (error) {
      console.error('Error fetching statistics:', error);
      toast.error('Failed to load statistics');
    }
  };

  // Responses are already grouped by API - just format for display
  const getGroupedResponses = () => {
    if (!responses || responses.length === 0) {
      return [];
    }
    
    // API already returns grouped data, just ensure responses are sorted by question display order
    return responses.map(submission => {
      // Sort responses by question display order if we have survey questions
      if (survey?.questions) {
        submission.responses.sort((a, b) => {
          const questionA = survey.questions.find(q => q.id === a.question_id);
          const questionB = survey.questions.find(q => q.id === b.question_id);
          const orderA = questionA?.display_order ?? a.question_id;
          const orderB = questionB?.display_order ?? b.question_id;
          return orderA - orderB;
        });
      }
      return submission;
    });
  };

  const renderStatistics = () => {
    if (!statistics) return null;

    return (
      <div>
        <StatsGrid theme={theme}>
          <StatCard theme={theme}>
            <StatLabel theme={theme}>Total Submissions</StatLabel>
            <StatValue theme={theme}>{statistics.total_submissions || 0}</StatValue>
          </StatCard>
        </StatsGrid>

        {statistics.question_statistics?.map((questionStat, index) => (
          <QuestionSection key={index} theme={theme}>
            <QuestionTitle theme={theme}>
              {questionStat.question_text}
            </QuestionTitle>
            <div>
              <ResponseCard theme={theme}>
                <StatLabel theme={theme}>Total Responses: {questionStat.total_responses}</StatLabel>
                {questionStat.option_counts && (
                  <div style={{ marginTop: '1rem' }}>
                    {Object.entries(questionStat.option_counts).map(([option, count]) => (
                      <OptionCount key={option} theme={theme}>
                        <div style={{ flex: 1 }}>{option}</div>
                        <div style={{ fontWeight: theme.typography.fontWeight.semibold }}>{count}</div>
                      </OptionCount>
                    ))}
                  </div>
                )}
              </ResponseCard>
            </div>
          </QuestionSection>
        ))}
      </div>
    );
  };

  const renderResponses = () => {
    const groupedResponses = getGroupedResponses();

    if (groupedResponses.length === 0) {
      return (
        <Card padding="3rem" textAlign="center">
          <MessageSquare size={48} style={{ opacity: 0.3, marginBottom: '1rem' }} />
          <p>No responses yet.</p>
        </Card>
      );
    }

    return groupedResponses.map((submission, index) => (
      <ResponseCard key={index} theme={theme}>
        <ResponseInfo>
          <RespondentInfo theme={theme}>
            <div style={{ fontWeight: theme.typography.fontWeight.medium }}>
              {submission.respondent_name || submission.respondent_email || 'Anonymous'}
            </div>
            {submission.respondent_email && (
              <div style={{ fontSize: theme.typography.fontSize.xs, marginTop: '0.25rem' }}>
                {submission.respondent_email}
              </div>
            )}
            <div style={{ fontSize: theme.typography.fontSize.xs, marginTop: '0.25rem' }}>
              {new Date(submission.submitted_at).toLocaleString()}
            </div>
          </RespondentInfo>
        </ResponseInfo>
        
        <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: `1px solid ${theme.colors.border}` }}>
          {submission.responses && submission.responses.length > 0 ? (
            submission.responses.map((response, idx) => (
              <div key={idx} style={{ marginBottom: '1rem' }}>
                <div style={{ 
                  fontSize: theme.typography.fontSize.sm, 
                  fontWeight: theme.typography.fontWeight.medium,
                  marginBottom: '0.5rem',
                  color: theme.colors.text.secondary
                }}>
                  {response.question_text || `Question ${response.question_id}`}
                </div>
                <ResponseText theme={theme}>
                  {response.answer || '[No response provided]'}
                </ResponseText>
              </div>
            ))
          ) : (
            <div style={{ color: theme.colors.text.secondary, fontStyle: 'italic' }}>
              No responses found for this submission.
            </div>
          )}
        </div>
      </ResponseCard>
    ));
  };

  const tabs = [
    { id: 'responses', label: 'Individual Responses', icon: Eye },
    { id: 'statistics', label: 'Statistics', icon: BarChart3 },
  ];

  if (loading && !survey) {
    return (
      <Container>
        <div style={{ textAlign: 'center', padding: '4rem' }}>
          <div>Loading...</div>
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <div>
          <Button
            variant="ghost"
            leftIcon={<ArrowLeft size={16} />}
            onClick={() => navigate('/app/surveys/manage')}
            style={{ marginBottom: '1rem' }}
          >
            Back to Surveys
          </Button>
          <Title theme={theme}>
            {survey?.title || 'Survey Responses'}
          </Title>
          {survey?.description && (
            <p style={{ color: theme.colors.text.secondary, marginTop: '0.5rem' }}>
              {survey.description}
            </p>
          )}
        </div>
      </Header>

      {/* Tabs */}
      <div style={{ 
        display: 'flex', 
        gap: '0.5rem', 
        marginBottom: '2rem',
        borderBottom: `1px solid ${theme.colors.border}`
      }}>
        {tabs.map(tab => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: '0.75rem 1.5rem',
                background: 'none',
                border: 'none',
                borderBottom: isActive ? `2px solid ${theme.colors.primary}` : '2px solid transparent',
                color: isActive ? theme.colors.primary : theme.colors.text.secondary,
                fontWeight: isActive ? theme.typography.fontWeight.semibold : theme.typography.fontWeight.medium,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: theme.typography.fontSize.base,
              }}
            >
              <Icon size={18} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      {activeTab === 'responses' ? renderResponses() : renderStatistics()}
    </Container>
  );
};

export default SurveyResponses;

